import React from "react";
import { Observer, useLocalStore, useObserver } from "mobx-react";
import { Paper } from "@material-ui/core";
import locale from "@bonliva-crm/locale";
import { PrimaryButton, SpinnerOverlay, TabBadge } from "@components";
import { useNavigation } from "@navigation";
import { Form, FormxDebugger } from "@common/formx";
import { SnapshotFormStore } from "./SnapshotFormStore";
import { initialValues } from "./utils";
import { defaultFields } from "./formSchemas";
import { TabFormLayout } from "@common/forms/TabFormLayout";
import { DynamicFieldsGroup } from "@consultants/ConsultantContract/DynamicFieldsGroup";

type Props = {
  contractId?: string;
  action?: string;
  snapshotId: string;
};

export const SnapshotForm = ({ snapshotId, contractId, action }: Props) => {
  const navigator = useNavigation();
  const localStore = useLocalStore(
    () =>
      new SnapshotFormStore(
        initialValues,
        contractId,
        () => handleClose(),
        snapshotId,
        action
      )
  );

  const handleClose = (): void => {
    localStore.form.reset({ ...initialValues, customerContractId: contractId });
    localStore.fields = defaultFields;
    setTimeout(() => {
      navigator.close();
    }, 200);
  };

  return useObserver(() => {
    const { form, loading, fields, isEdit } = localStore;

    const renderMain = (
      <Paper
        elevation={0}
        style={{
          height: "100%",
          padding: "12px 24px 36px",
          position: "relative"
        }}
      >
        <SpinnerOverlay show={loading} />
        <DynamicFieldsGroup
          form={form}
          fields={fields}
          extraProps={{ store: localStore }}
        />
      </Paper>
    );

    const tabs = [
      {
        label: (
          <TabBadge
            label={isEdit ? locale.Edit : `${locale.New} ${locale.Contract}`}
            hasErrors={false}
          />
        ),
        value: "main",
        content: renderMain
      }
    ];

    const extraBtns = (
      <Observer>
        {() => (
          <PrimaryButton
            onClick={() => localStore.saveAndSendToOneflow()}
            disabled={!form.canSubmit || form.isSubmitting}
            loading={form.isSubmitting}
          >
            Skapa och skicka till OneFlow
          </PrimaryButton>
        )}
      </Observer>
    );

    return (
      <Form store={form} allowUpdates>
        <TabFormLayout
          blockId="customer_contract_snapshot_form"
          canSubmit={form.canSubmit}
          isDirty={form.dirty}
          isSubmitting={form.isSubmitting}
          onSubmit={localStore.onSubmit}
          onReset={form.onResetHandler}
          extraButtons={extraBtns}
          onClose={localStore.handleClose}
          tabs={tabs}
        >
          <FormxDebugger store={localStore.form} />
        </TabFormLayout>

        <FormxDebugger store={form} />
      </Form>
    );
  });
};
